/* eslint-disable max-lines */

import { OverlayContainer } from '@angular/cdk/overlay';
import { APP_BASE_HREF, IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, inject, NgZone, Provider, signal } from '@angular/core';
import { ɵSharedStylesHost } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';

import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';

import { Analytics, AnalyticsClient, AnalyticsConfig } from '@core/analytics-types';
import {
  templatesEffectsProvider,
  templatesStoreProvider,
  predefinedTemplatesStoreProvider,
  predefinedTemplatesEffectsProvider,
} from '@core/ask-hr-templates-data';
import {
  CHAT_FORM_FEATURE,
  CHAT_FORMS_LIST_FEATURE,
  provideAssistantChatRequestItem,
} from '@core/ask-hr-templates-smart-ui';
import { askTicketsEffectsProvider, askTicketsStoreProvider } from '@core/ask-hr-tickets-data';
import {
  CHAT_TICKETS_LIST_FEATURE,
  provideAssistantChatTicketItem,
} from '@core/ask-hr-tickets-smart-ui';
import {
  APP_CONFIG,
  AUTH_METHOD,
  COMPANY_ID,
  ENVIRONMENT,
  LocalStorageKeyBuilder,
  NoopNgZone,
} from '@core/common-legacy';
import {
  AuthenticationDialogService,
  PasswordChangeService,
  provideAuthTranslations,
  TwoFaService,
} from '@core/components-authorization-legacy';
import {
  countriesData,
  COUNTRY_STORE_CONFIG,
  countryStoreConfigFactory,
  injectCountryList,
} from '@core/country-data';
import {
  AuthorisationStrategy,
  CompanySwitchStrategy,
  CookieService,
  GenericAuthService,
  HttpApiErrorHandlerService,
} from '@core/http-legacy';
import { HttpRequestData } from '@core/http-types';
import {
  I18N_CONFIG_TOKEN,
  I18nService,
  LANGUAGE_CONFIG,
  LanguageConfig,
  LOCALE,
  PhraseAppEditorService,
  PhraseAppTranslationService,
  TranslationsProvider,
} from '@core/i18n-legacy';
import {
  provideHrAssistantConfigNgrx,
  provideIntelligenceSettingsNgrx,
} from '@core/intelligence-data';
import { provideSymbolPickerTranslations } from '@core/symbol-legacy';
import { hiringResourcesData } from '@core/tellent-assign-members-to-jobs-data';
import { companyVerificationData } from '@core/tellent-company-verification-data';
import {
  featureDiscoveryStoreProviders,
  tellentAppsData,
  tellentCurrentUsersData,
  tellentGrantAccessData,
  tellentGroupsData,
  tellentMembersData,
  TellentNavigationEffects,
  TellentNavigationStateProvider,
  tellentRolesData,
  tellentSecureChangeEffects,
  tellentTwoFaData,
} from '@core/tellent-data';
import { tellentDevFlagsData } from '@core/tellent-dev-flags-data';
import {
  TellentAdminHttpApiErrorHandlerService,
  TellentAdminRequestData,
} from '@core/tellent-http-legacy';
import {
  tellentMentionablesEffectsProvider,
  tellentMentionablesStoreProvider,
  tellentNotesEffectsProvider,
  tellentNotesStoreProvider,
} from '@core/tellent-notes-data';
import {
  TellentAdminAuthenticationDialogService,
  TellentAdminPasswordChangeService,
  TellentAppTwoFaService,
  tellentAuthModuleTranslations,
} from '@core/tellent-smart-ui';
import {
  provideTellentNavigationScope,
  TELLENT_ADOPTED_STYLESHEETS,
  TELLENT_CLIENT_CONFIG,
  TellentClientAppConfig,
  TellentClientConfig,
} from '@core/tellent-types';
import { TellentComponentsNotificationService } from '@core/tellent-util';
import {
  IconsContainerBundledStrategy,
  PHONE_INPUT_DATA,
  protectedControlsRootProviders,
  provideIconsContainerStrategy,
} from '@core/user-interface-legacy';
import { viewsDataProviders } from '@core/views-data';
import { viewEffectsProvider } from '@core/views-legacy';

import { environments } from './app.environments';
import { environment } from '../environments/environment';
import { TellentClientAuthStrategy } from './auth/tellent-client-auth.strategy';
import { TellentClientBootstrap } from './bootstrap';
import { TellentClientCompanySwitchStrategy } from './services/company-switch-strategy.service';
import { CustomStylesHostService } from './services/custom-styles-host.service';
import { TellentClientLocalStorageKeyBuilder } from './services/local-storage-key-builder.service';
import { TellentClientIconsContainer } from './shadow-root/services/icons-container';
import { ShadowOverlayContainer } from './shadow-root/services/shadow-overlay-container';

if (environment.angularProdMode) {
  enableProdMode();
}

export const getTellentClientEnv = (env: TellentClientConfig['env']) => {
  const {
    tellentAuthUrl,
    tellentApiUrl,
    tellentAppUrl,
    tellentApiWsUrl,
    tellentOneDomainUrl,
    reportsApiUrl,
  } = environments[env];

  return {
    tellentAuthTokenUrl: `${tellentAuthUrl}/access-token`,
    tellentUserUrl: `${tellentApiUrl}/api/me`,
    tellentApiUrl: tellentApiUrl,
    tellentApiWsUrl: tellentApiWsUrl,
    tellentAuthUrl: tellentAuthUrl,
    tellentAppUrl: tellentAppUrl,
    tellentOneDomainUrl: tellentOneDomainUrl,
    reportsApiUrl,
  };
};

export const tellentClientAppConfig = (appConfig: TellentClientConfig): TellentClientAppConfig => {
  const env = getTellentClientEnv(appConfig.env);

  return {
    ...env,
    headers: {},
  };
};

export const StoreDataProviders = [
  provideStore(),
  TellentNavigationStateProvider,
  provideEffects([TellentNavigationEffects]),
];

export const httpProviders = [
  { provide: AuthorisationStrategy, useClass: TellentClientAuthStrategy },
  { provide: TellentAdminHttpApiErrorHandlerService },
  { provide: HttpApiErrorHandlerService, useExisting: TellentAdminHttpApiErrorHandlerService },
  provideHttpClient(withInterceptorsFromDi()),
];

const translationProviders = [
  { provide: TranslationsProvider, useExisting: PhraseAppTranslationService },
  { provide: LOCALE, useValue: 'en' },
  {
    provide: I18N_CONFIG_TOKEN,
    useValue: {
      editorEnabled: false,
      phraseAppProjectId: '',
    },
  },
  {
    provide: LANGUAGE_CONFIG,
    useValue: <LanguageConfig>{
      defaultLanguage: 'en',
      languages: ['en', 'de', 'fr', 'nl'],
    },
  },
  I18nService,
  PhraseAppTranslationService,
  PhraseAppEditorService,
  provideSymbolPickerTranslations({
    useFactory: (i18n = inject(I18nService)) => ({
      tabEmojis: i18n.translate('tlc.emoji.emojis'),
      tabIcons: i18n.translate('tlc.emoji.icons'),
      searchPlaceholder: i18n.translate('tlc.emoji.search'),
      clear: i18n.translate('tlc.common.clear'),
      categories: {
        recentlyUsed: i18n.translate('tlc.emoji.category.recently_used'),
        smileysEmotion: i18n.translate('tlc.emoji.category.smileys_emotion'),
        peopleBody: i18n.translate('tlc.emoji.category.people_body'),
        animalsNature: i18n.translate('tlc.emoji.category.animals_nature'),
        foodDrink: i18n.translate('tlc.emoji.category.food_drink'),
        travelPlaces: i18n.translate('tlc.emoji.category.travel_places'),
        activities: i18n.translate('tlc.emoji.category.activities'),
        objects: i18n.translate('tlc.emoji.category.objects'),
        symbols: i18n.translate('tlc.emoji.category.symbols'),
        flags: i18n.translate('tlc.emoji.category.flags'),
        skinTones: i18n.translate('tlc.emoji.category.skin_tones'),
        tabIcons: i18n.translate('tlc.emoji.icons'),
      },
    }),
  }),
];

const noopAnalyticsHandlers: TellentClientConfig['analyticsCallbacks'] = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  reset: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  track: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  alias: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  identify: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  page: () => {},
};

const noopToastHandlers: TellentClientConfig['toastCallbacks'] = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  success: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  error: () => {},
};

const initConfigProviders = (initConfig: TellentClientConfig) => [
  { provide: COMPANY_ID, useValue: null },
  { provide: CookieService, useClass: CookieService },
  provideAnimations(),
  {
    provide: TellentComponentsNotificationService,
    useValue: initConfig.toastCallbacks ?? noopToastHandlers,
  },
  {
    provide: AnalyticsClient,
    useValue: initConfig.analyticsCallbacks ?? noopAnalyticsHandlers,
  },
  {
    provide: Analytics,
    useExisting: AnalyticsClient,
  },
  {
    provide: AnalyticsConfig,
    useValue: {
      defaultSegmentOptions: {},
      opts: {
        trackRouteChange: false,
      },
    },
  },
];

const storeDateProviders = [
  tellentCurrentUsersData,
  tellentRolesData,
  tellentMembersData,
  tellentTwoFaData,
  countriesData,
  tellentAppsData,
  tellentGrantAccessData,
  tellentSecureChangeEffects,
  tellentGroupsData,
  tellentDevFlagsData,
  companyVerificationData,
  hiringResourcesData,
  ...featureDiscoveryStoreProviders,
  ...provideHrAssistantConfigNgrx,
  ...provideIntelligenceSettingsNgrx,
  viewsDataProviders,
  viewEffectsProvider,
  templatesStoreProvider,
  templatesEffectsProvider,
  predefinedTemplatesStoreProvider,
  predefinedTemplatesEffectsProvider,
  askTicketsStoreProvider,
  askTicketsEffectsProvider,
  tellentNotesStoreProvider,
  tellentNotesEffectsProvider,
  tellentMentionablesStoreProvider,
  tellentMentionablesEffectsProvider,
];

export const bootstrapInjectorProviders = (config: TellentClientConfig): Provider[] => [
  { provide: TELLENT_CLIENT_CONFIG, useValue: config },
  { provide: APP_CONFIG, useValue: tellentClientAppConfig(config) },
  { provide: NgZone, useClass: NoopNgZone },
  { provide: ɵSharedStylesHost, useClass: CustomStylesHostService },
  { provide: OverlayContainer, useClass: ShadowOverlayContainer },
  { provide: APP_BASE_HREF, useValue: config.baseHref ?? '/' },
  {
    provide: ENVIRONMENT,
    useValue: environment,
  },
  { provide: AUTH_METHOD, useValue: 'Bearer' },
  { provide: CookieService },
  { provide: TellentClientBootstrap, useClass: TellentClientBootstrap },
  {
    provide: HttpRequestData,
    useClass: TellentAdminRequestData,
  },
  { provide: GenericAuthService, useClass: GenericAuthService },
  { provide: AuthorisationStrategy, useClass: TellentClientAuthStrategy },
  { provide: TellentClientIconsContainer, useFactory: () => new TellentClientIconsContainer() },
  {
    provide: CompanySwitchStrategy,
    useClass: TellentClientCompanySwitchStrategy,
  },
  CHAT_FORMS_LIST_FEATURE,
  CHAT_FORM_FEATURE,
  CHAT_TICKETS_LIST_FEATURE,
  provideAssistantChatRequestItem,
  provideAssistantChatTicketItem,
];

export const userProfileProviders = [
  provideAuthTranslations({ useValue: tellentAuthModuleTranslations }),
  {
    provide: COUNTRY_STORE_CONFIG,
    useFactory: countryStoreConfigFactory,
  },
  {
    provide: PHONE_INPUT_DATA,
    useFactory: injectCountryList,
  },
  { provide: AuthenticationDialogService, useExisting: TellentAdminAuthenticationDialogService },
  { provide: PasswordChangeService, useExisting: TellentAdminPasswordChangeService },
  { provide: TwoFaService, useExisting: TellentAppTwoFaService },
];

export const resolveAppProviders = (initConfig: TellentClientConfig) => {
  const providers: Provider[] = [
    provideTellentNavigationScope({ useValue: initConfig.navigationScope }),
    { provide: TELLENT_CLIENT_CONFIG, useValue: initConfig },
    { provide: TELLENT_ADOPTED_STYLESHEETS, useValue: signal([]) },
    { provide: LocalStorageKeyBuilder, useClass: TellentClientLocalStorageKeyBuilder },
    provideIconsContainerStrategy({ useClass: IconsContainerBundledStrategy }),
    initConfigProviders(initConfig),
    protectedControlsRootProviders(),
    userProfileProviders,
    translationProviders,
    httpProviders,
    StoreDataProviders,
    storeDateProviders,
  ];

  if (environment.assetsCdnUrl) {
    providers.push({
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => `${environment.assetsCdnUrl}/${config.src}`,
    });
  }

  return providers;
};
